import {
  CityModel,
  FormAddress,
  FormRisk,
  FormRiskTravel,
  FormTruck,
  FormTruckComposition,
  FormTruckCompositionOwner,
  FormTrucker,
  RiskAddressModel,
  RiskCnhModel,
  RiskModel,
  RiskTravelModel,
  RiskTruckCompositionOwnerModel,
  RiskTruckModel,
  RiskTruckerModel,
} from '@/types';
import { dateYmdToDmy } from '@/utils';
import { valueOrEmptyString } from '@/utils/string';

const getLocation = (city: string, state: string): CityModel => {
  const name = valueOrEmptyString(city);
  const stateAbbr = valueOrEmptyString(state);
  const place = name && stateAbbr ? `${name} - ${stateAbbr}` : '';

  return { name, stateAbbr, place };
};

const getAddress = (address: RiskAddressModel): FormAddress => {
  const { state, city, ...data } = address;
  return {
    location: getLocation(city, state),
    number: valueOrEmptyString(data.number),
    street: valueOrEmptyString(data.street),
    zipCode: valueOrEmptyString(data.zipCode),
    complement: valueOrEmptyString(data.complement),
    neighborhood: valueOrEmptyString(data.neighborhood),
  };
};

const getParsedCnh = (cnh: RiskCnhModel): RiskCnhModel => ({
  ...cnh,
  dateFirstLicense: dateYmdToDmy(cnh.dateFirstLicense),
  expirationDate: dateYmdToDmy(cnh.expirationDate),
});

const getCompositionOwner = (owner: RiskTruckCompositionOwnerModel): FormTruckCompositionOwner => {
  const { address, birthdate, ...data } = owner;

  return {
    address: getAddress(address),
    birthdate: !!birthdate ? dateYmdToDmy(birthdate) : '',
    cellPhone: valueOrEmptyString(data.cellPhone),
    cpfCnpj: valueOrEmptyString(data.cpfCnpj),
    motherName: valueOrEmptyString(data.motherName),
    name: valueOrEmptyString(data.name),
    rntrc: valueOrEmptyString(data.rntrc),
    rntrcCpfCnpj: valueOrEmptyString(data.rntrcCpfCnpj),
    rntrcName: valueOrEmptyString(data.rntrcName),
    rntrcOwnerIsTruckOwner: !!data.rntrcOwnerIsTruckOwner,
  };
};

const getParsedTruck = (model: RiskTruckModel): FormTruck => {
  const { compositions, ...restOfData } = model;

  const localCompositions: FormTruckComposition[] = compositions.map((item) => {
    const { owner, city, state, plateApi, color, vehicleIsLeased, ...restOfItem } = item;

    return {
      brand: valueOrEmptyString(restOfItem.brand),
      chassis: valueOrEmptyString(restOfItem.chassis),
      color: color ? `${color.charAt(0).toUpperCase()}${color.slice(1).toLowerCase()}` : '',
      leaserCpfCnpj: valueOrEmptyString(restOfItem.leaserCpfCnpj),
      leaserName: valueOrEmptyString(restOfItem.leaserName),
      location: getLocation(city, state),
      model: valueOrEmptyString(restOfItem.model),
      modelYear: valueOrEmptyString(restOfItem.modelYear),
      monofactureYear: valueOrEmptyString(restOfItem.monofactureYear),
      owner: getCompositionOwner(owner),
      plate: valueOrEmptyString(restOfItem.plate),
      plateApi: plateApi || false,
      renavam: valueOrEmptyString(restOfItem.renavam),
      truckerIsOwner: !!restOfItem.truckerIsOwner,
      typeId: valueOrEmptyString(restOfItem.typeId),
      typeName: valueOrEmptyString(restOfItem.typeName),
      uuid: valueOrEmptyString(restOfItem.uuid),
      vehicleIsLeased: vehicleIsLeased || false,
    };
  });

  return {
    compositions: localCompositions,
    typeId: valueOrEmptyString(restOfData.typeId),
    typeName: valueOrEmptyString(restOfData.typeName),
  };
};

const getParsedTrucker = (model: RiskTruckerModel): FormTrucker => {
  const { address, birthday, fatherName, cnh, ...restOfData } = model;

  return {
    ...restOfData,
    address: getAddress(address),
    birthday: dateYmdToDmy(birthday),
    cnh: getParsedCnh(cnh),
    fatherName: fatherName || '',
  };
};

const getParsedTravel = (model: RiskTravelModel): FormRiskTravel => {
  const { freightId = '', cargoType, cargoValue, destinationCity, destinationState, originCity, originState } = model;

  return {
    freightId,
    cargoType,
    cargoValue,
    destinationPlace: getLocation(destinationCity, destinationState),
    originPlace: getLocation(originCity, originState),
  };
};

export const parseRiskModelToForm = (model: RiskModel): FormRisk => ({
  truck: getParsedTruck(model.truck),
  trucker: getParsedTrucker(model.trucker),
  travel: getParsedTravel(model.travel),
  uuid: model.uuid,
  partnerId: model.partnerId,
});
